import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { samplesApi, productsApi, warehouseApi, authHelpers } from '../services/api';
import {
  Search, Filter, FileText, Download, Plus,
  Leaf, Activity, Sparkles, PlusCircle, Target, ClipboardList, X, Loader2, AlertTriangle
} from 'lucide-react';

const PRODUCT_ICONS = ['leaf', 'flower', 'pulse', 'bottle'];

function StockSampling() {
  const { currentTheme } = useTheme();
  const isDark = ['Purple', 'Black'].includes(currentTheme?.name);

  // ── API DATA STATES ──
  const [samplesList, setSamplesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [warehousesList, setWarehousesList] = useState([]);
  const [summary, setSummary] = useState(null); // { totalSamplesDistributed, highValueSamples }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterProduct, setFilterProduct] = useState('All');
  const [filterWarehouse, setFilterWarehouse] = useState('All');
  const [filterApprover, setFilterApprover] = useState('All');
  const [filterRole, setFilterRole] = useState('All');
  const [filterCreatedBy, setFilterCreatedBy] = useState('All');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Register New Sample modal/form state
  const [showModal, setShowModal] = useState(false);
  const [newSample, setNewSample] = useState({
    productId: '',
    quantity: '',
    receiverName: '',
    receiverDesignation: '',
    warehouseId: '',
    approverName: '',
    remarks: '',
  });

  // ── Backend Sample → UI shape mapper ──
  const mapSample = (s, idx) => ({
    id: s.id ?? idx,
    recordId: s.recordId || `SMP-${String(idx + 1).padStart(3, '0')}`,
    date: s.sampleDate
      ? new Date(s.sampleDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : (s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''),
    dateRaw: s.sampleDate || s.createdAt,
    productId: s.productId,
    productName: s.productName || '',
    productIcon: PRODUCT_ICONS[(s.productId || 0) % PRODUCT_ICONS.length],
    qty: `${s.quantity ?? 0} Units`,
    recipientName: s.receiverName || '',
    recipientRole: s.receiverDesignation || 'N/A',
    warehouseId: s.warehouseId,
    source: s.warehouseName || 'N/A',
    createdBy: s.createdByName || '',
    approver: s.approverName || 'N/A',
    isHighValue: !!s.isHighValue,
  });

  // ── FETCH: Samples list + dropdowns + summary ──
  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [samplesRes, productsRes, warehousesRes, summaryRes] = await Promise.all([
        samplesApi.getAll(0, 1000),
        productsApi.getAllProducts().catch(() => []),
        warehouseApi.getActiveWarehouses().catch(() => []),
        samplesApi.getSummary().catch(() => null),
      ]);

      const list = samplesRes?.entries || samplesRes?.content || samplesRes || [];
      setSamplesList((Array.isArray(list) ? list : []).map(mapSample));

      const productArr = Array.isArray(productsRes) ? productsRes : (productsRes?.content || productsRes?.data || []);
      setProductsList(Array.isArray(productArr) ? productArr : []);

      setWarehousesList(Array.isArray(warehousesRes) ? warehousesRes : []);

      if (summaryRes) setSummary(summaryRes);
    } catch (err) {
      setError(err.message || 'Failed to load samples');
      console.error('Samples load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Dynamic Recipient Roles / Approvers / Creators for filter options (loaded data se)
  const rolesList = useMemo(() => {
    const roles = samplesList.map(item => item.recipientRole).filter(Boolean);
    return [...new Set(roles)].sort();
  }, [samplesList]);

  const approversList = useMemo(() => {
    const approvers = samplesList.map(item => item.approver).filter(Boolean);
    return [...new Set(approvers)].sort();
  }, [samplesList]);

  const creatorsList = useMemo(() => {
    const creators = samplesList.map(item => item.createdBy).filter(Boolean);
    return [...new Set(creators)].sort();
  }, [samplesList]);

  const hasActiveFilters = useMemo(() => {
    return filterProduct !== 'All' ||
           filterWarehouse !== 'All' ||
           filterApprover !== 'All' ||
           filterRole !== 'All' ||
           filterCreatedBy !== 'All' ||
           filterDateFrom !== '' ||
           filterDateTo !== '';
  }, [filterProduct, filterWarehouse, filterApprover, filterRole, filterCreatedBy, filterDateFrom, filterDateTo]);

  const renderProductIcon = (type) => {
    const classes = "w-4 h-4 text-emerald-600 dark:text-emerald-400";
    switch (type) {
      case 'leaf': return <Leaf className={classes} />;
      case 'flower': return <Sparkles className={classes} />;
      case 'pulse': return <Activity className={classes} />;
      case 'bottle':
      default: return <PlusCircle className={classes} />;
    }
  };

  // Search & Filter logic (client-side — backend /api/samples ke paas filter params nahi hain)
  const filteredSamples = useMemo(() => {
    return samplesList.filter(item => {
      const matchSearch =
        item.recordId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.recipientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.source.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchSearch) return false;
      if (filterProduct !== 'All' && item.productName !== filterProduct) return false;
      if (filterWarehouse !== 'All' && item.source !== filterWarehouse) return false;
      if (filterApprover !== 'All' && item.approver !== filterApprover) return false;
      if (filterRole !== 'All' && item.recipientRole !== filterRole) return false;
      if (filterCreatedBy !== 'All' && item.createdBy !== filterCreatedBy) return false;

      if (filterDateFrom) {
        const itemDate = new Date(item.dateRaw);
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        itemDate.setHours(0, 0, 0, 0);
        if (itemDate < fromDate) return false;
      }
      if (filterDateTo) {
        const itemDate = new Date(item.dateRaw);
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        itemDate.setHours(0, 0, 0, 0);
        if (itemDate > toDate) return false;
      }
      return true;
    });
  }, [samplesList, searchTerm, filterProduct, filterWarehouse, filterApprover, filterRole, filterCreatedBy, filterDateFrom, filterDateTo]);

  // ── Export (backend endpoint pending) ──
  const handleExport = (format) => {
    alert(`${format.toUpperCase()} export ke liye backend endpoint abhi available nahi hai.`);
  };

  // ── Handle New Sample Submission (API) ──
  const handleAddSample = async (e) => {
    e.preventDefault();
    if (!newSample.productId || !newSample.quantity || !newSample.receiverName || !newSample.warehouseId) {
      alert('Please fill out all required fields (Product, Quantity, Receiver Name, Warehouse).');
      return;
    }

    setCreating(true);
    try {
      await samplesApi.create({
        productId: Number(newSample.productId),
        quantity: parseInt(newSample.quantity) || 0,
        receiverName: newSample.receiverName,
        receiverDesignation: newSample.receiverDesignation || null,
        warehouseId: Number(newSample.warehouseId),
        approverName: newSample.approverName || null,
        createdByName: authHelpers.getUserName() || 'Unknown',
        remarks: newSample.remarks || null,
        isHighValue: false,
      });

      setShowModal(false);
      setNewSample({
        productId: '', quantity: '', receiverName: '', receiverDesignation: '',
        warehouseId: '', approverName: '', remarks: '',
      });
      loadAll(); // list + summary refresh
    } catch (err) {
      alert(err.message || 'Failed to register sample');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={`min-h-screen p-4 desktop:p-8 space-y-6 transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>

      {/* ── HEADER ── */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className={`text-2xl desktop:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Sampling
            </h1>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-555'} mt-1`}>
              {loading ? 'Loading...' : `${filteredSamples.length} samples · All warehouses`}
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search record ID, product, recipient..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 pr-4 py-2 rounded-xl text-xs w-full outline-none border focus:border-emerald-500 transition-all ${
                isDark
                  ? 'bg-slate-900 border-slate-800 text-white placeholder-slate-500'
                  : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 shadow-sm'
              }`}
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-xl border hover:opacity-85 transition-all cursor-pointer relative ${
              showFilters
                ? 'bg-emerald-50 border-emerald-300 text-emerald-600 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-400'
                : isDark
                  ? 'bg-slate-900 border-slate-800 text-white'
                  : 'bg-white border-gray-200 text-gray-770 shadow-sm'
            }`}
          >
            <Filter className="w-4 h-4" />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full border border-white dark:border-slate-900 animate-pulse" />
            )}
          </button>

          <button
            onClick={() => handleExport('pdf')}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-bold hover:opacity-85 transition-all cursor-pointer ${
              isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750 shadow-sm'
            }`}
          >
            <FileText className="w-3.5 h-3.5 text-rose-500" />
            PDF
          </button>

          <button
            onClick={() => handleExport('excel')}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-bold hover:opacity-85 transition-all cursor-pointer ${
              isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750 shadow-sm'
            }`}
          >
            <Download className="w-3.5 h-3.5 text-emerald-600" />
            Export Excel
          </button>

          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-all shadow-md cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: currentTheme.color }}
          >
            <Plus className="w-3.5 h-3.5" />
            New Sample
          </button>
        </div>
      </div>

      {/* ── ERROR BANNER ── */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={loadAll} className="ml-auto text-xs font-black uppercase tracking-wider underline cursor-pointer">
            Retry
          </button>
        </div>
      )}

      {/* ── COLLAPSIBLE FILTERS PANEL ── */}
      {showFilters && (
        <div className={`p-5 rounded-2xl border transition-all duration-300 relative ${
          isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-100 dark:border-slate-800/60">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span className={`text-xs font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Filter Samples
              </span>
            </div>
            <div className="flex items-center gap-3">
              {hasActiveFilters && (
                <button
                  onClick={() => {
                    setFilterProduct('All');
                    setFilterWarehouse('All');
                    setFilterApprover('All');
                    setFilterRole('All');
                    setFilterCreatedBy('All');
                    setFilterDateFrom('');
                    setFilterDateTo('');
                  }}
                  className="text-[10px] font-bold text-rose-500 hover:text-rose-600 transition-colors uppercase tracking-wider cursor-pointer"
                >
                  Clear All
                </button>
              )}
              <button
                onClick={() => setShowFilters(false)}
                className="p-1 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                title="Close filters"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-xs">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Product</label>
              <select
                value={filterProduct}
                onChange={(e) => setFilterProduct(e.target.value)}
                className={`w-full px-3 py-2 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                }`}
              >
                <option value="All">All Products</option>
                {productsList.map(p => (
                  <option key={p.id} value={p.productName || p.name}>{p.productName || p.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Warehouse Source</label>
              <select
                value={filterWarehouse}
                onChange={(e) => setFilterWarehouse(e.target.value)}
                className={`w-full px-3 py-2 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                }`}
              >
                <option value="All">All Warehouses</option>
                {warehousesList.map(wh => (
                  <option key={wh.id} value={wh.name}>{wh.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Approver</label>
              <select
                value={filterApprover}
                onChange={(e) => setFilterApprover(e.target.value)}
                className={`w-full px-3 py-2 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                }`}
              >
                <option value="All">All Approvers</option>
                {approversList.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Recipient Role</label>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className={`w-full px-3 py-2 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                }`}
              >
                <option value="All">All Roles</option>
                {rolesList.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Created By</label>
              <select
                value={filterCreatedBy}
                onChange={(e) => setFilterCreatedBy(e.target.value)}
                className={`w-full px-3 py-2 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                }`}
              >
                <option value="All">All Creators</option>
                {creatorsList.map(creator => (
                  <option key={creator} value={creator}>{creator}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Date From</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className={`w-full px-3 py-2 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                }`}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Date To</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className={`w-full px-3 py-2 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                }`}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── STAT CARDS ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className={`p-5 rounded-2xl border flex items-center gap-4 ${
          isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
        }`}>
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
            <ClipboardList className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">TOTAL SAMPLES DISTRIBUTED</p>
            <p className={`text-xl font-black mt-0.5 ${isDark ? 'text-white' : 'text-gray-955'}`}>
              {loading ? '—' : Number(summary?.totalSamplesDistributed ?? samplesList.length).toLocaleString('en-IN')}
            </p>
          </div>
        </div>

        <div className={`p-5 rounded-2xl border flex items-center gap-4 ${
          isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
        }`}>
          <div className="w-10 h-10 rounded-xl bg-[#fdf5f2] text-amber-600 dark:bg-amber-950/20 dark:text-amber-450 flex items-center justify-center flex-shrink-0">
            <Target className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">HIGH-VALUE SAMPLES</p>
            <p className={`text-xl font-black mt-0.5 ${isDark ? 'text-white' : 'text-gray-955'}`}>
              {loading ? '—' : Number(summary?.highValueSamples ?? 0).toLocaleString('en-IN')}
            </p>
          </div>
        </div>
      </div>

      {/* ── DATA TABLE ── */}
      <div className={`border rounded-2xl p-6 transition-all ${
        isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
      }`}>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-100 dark:border-slate-800/50 text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-slate-500 pb-3">
                <th className="pb-3 pl-2">RECORD ID</th>
                <th className="pb-3">DATE</th>
                <th className="pb-3">PRODUCT NAME</th>
                <th className="pb-3 text-center">QTY</th>
                <th className="pb-3">RECIPIENT</th>
                <th className="pb-3">WAREHOUSE SOURCE</th>
                <th className="pb-3">CREATED BY</th>
                <th className="pb-3 pr-2">APPROVER</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800/40 text-xs">
              {loading ? (
                <tr>
                  <td colSpan="8" className="py-12 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mx-auto mb-2" />
                    <span className="text-gray-400 text-sm">Loading samples...</span>
                  </td>
                </tr>
              ) : filteredSamples.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-12 text-center text-gray-400 text-sm font-semibold">
                    No samples found matching the filters.
                  </td>
                </tr>
              ) : (
                filteredSamples.map(item => (
                  <tr key={item.id} className="hover:bg-slate-50/20 dark:hover:bg-slate-900/5 transition-colors">
                    <td className="py-5 pl-2 font-black text-emerald-650 dark:text-emerald-400">
                      {item.recordId}
                    </td>
                    <td className="py-5 text-gray-500 dark:text-slate-450 font-bold whitespace-nowrap">
                      {item.date}
                    </td>
                    <td className="py-5 font-bold text-gray-700 dark:text-slate-300">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                          {renderProductIcon(item.productIcon)}
                        </div>
                        <span className="text-gray-900 dark:text-white font-bold">{item.productName}</span>
                      </div>
                    </td>
                    <td className="py-5 text-center font-black text-emerald-600 dark:text-emerald-450">
                      {item.qty}
                    </td>
                    <td className="py-5 text-gray-900 dark:text-white font-bold">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-800 dark:text-white">{item.recipientName}</span>
                        <span className="text-[10px] text-gray-400 font-medium mt-0.5">{item.recipientRole}</span>
                      </div>
                    </td>
                    <td className="py-5 text-gray-600 dark:text-slate-400 font-bold">
                      {item.source}
                    </td>
                    <td className="py-5 text-gray-700 dark:text-slate-350 font-bold">
                      {item.createdBy}
                    </td>
                    <td className="py-5 pr-2 text-gray-700 dark:text-slate-350 font-bold">
                      {item.approver}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── REGISTER NEW SAMPLE MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-all duration-300">
          <div className={`w-full max-w-2xl p-6 rounded-2xl border shadow-2xl relative animate-fade-in ${
            isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-150 text-gray-900'
          }`}>
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>

            <h3 className={`text-base font-black tracking-tight mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Register New Sample
            </h3>

            <form onSubmit={handleAddSample} className="space-y-5 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Receiver Name</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Dr. Meenal Joshi"
                    value={newSample.receiverName}
                    onChange={(e) => setNewSample({ ...newSample, receiverName: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-950 border-slate-800 text-white placeholder-slate-600' : 'bg-[#fbfbf8] border-gray-200 text-gray-800 placeholder-gray-400'
                    }`}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Receiver Designation</label>
                  <input
                    type="text"
                    placeholder="e.g. General Practitioner"
                    value={newSample.receiverDesignation}
                    onChange={(e) => setNewSample({ ...newSample, receiverDesignation: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955 border-slate-800 text-white placeholder-slate-600' : 'bg-[#fbfbf8] border-gray-200 text-gray-800 placeholder-gray-400'
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Product Name</label>
                  <select
                    required
                    value={newSample.productId}
                    onChange={(e) => setNewSample({ ...newSample, productId: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                    }`}
                  >
                    <option value="">Select Product</option>
                    {productsList.map(p => (
                      <option key={p.id} value={p.id}>{p.productName || p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Quantity</label>
                  <input
                    type="number"
                    required
                    placeholder="0"
                    value={newSample.quantity}
                    onChange={(e) => setNewSample({ ...newSample, quantity: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955 border-slate-800 text-white placeholder-slate-600' : 'bg-[#fbfbf8] border-gray-200 text-gray-800 placeholder-gray-400'
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Approver Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Lalit Mohan Sharma"
                    value={newSample.approverName}
                    onChange={(e) => setNewSample({ ...newSample, approverName: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                    }`}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Warehouse Source</label>
                  <select
                    required
                    value={newSample.warehouseId}
                    onChange={(e) => setNewSample({ ...newSample, warehouseId: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-[#fbfbf8] border-gray-200 text-gray-800'
                    }`}
                  >
                    <option value="">Select Warehouse</option>
                    {warehousesList.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Remarks</label>
                <textarea
                  rows={3}
                  placeholder="Add any additional notes..."
                  value={newSample.remarks}
                  onChange={(e) => setNewSample({ ...newSample, remarks: e.target.value })}
                  className={`w-full p-4 rounded-xl border outline-none focus:border-emerald-500 transition-all resize-none ${
                    isDark ? 'bg-slate-955 border-slate-800 text-white placeholder-slate-650' : 'bg-[#fbfbf8] border-gray-200 text-gray-800 placeholder-gray-400'
                  }`}
                />
              </div>

              <div className="flex justify-end items-center gap-4 pt-4 border-t border-gray-100 dark:border-slate-800/40">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={creating}
                  className={`px-5 py-2.5 rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-slate-850 transition-all cursor-pointer disabled:opacity-50 ${
                    isDark ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-white font-bold hover:opacity-90 transition-all shadow-md cursor-pointer disabled:opacity-60"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {creating ? 'Registering...' : 'Register Sample'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default StockSampling;